import type { QuizBlock } from '@/types/blocks'
import { Label, Textarea, cn } from '@teach/ui'
import { QUIZ_LIMITS } from '@/features/editor/constants/fieldLimits'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { charsOk } from '@/features/editor/lib/quizWordLimits'
import { getBody, setQuestion } from './quizBodySync'
import { CharCount } from './shared/QuizFieldCounters'
import { QuizFeedbackFields } from './QuizFeedbackFields'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: QuizBlock
  onChange: (next: QuizBlock) => void
}

// yes_no: a statement + which of Да/Нет is correct. correctAnswer is a single
// block-level boolean (true = Да), NOT localized. answers stay [] (backend
// synthesizes the two options on create).
export const YesNoQuizEditor = ({ block, onChange }: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const lang = useContentLang()

  const options: Array<{ value: boolean; label: string }> = [
    { value: true, label: m.yes },
    { value: false, label: m.no },
  ]

  return (
    <div className="space-y-4">
      <section className="space-y-1">
        <Label htmlFor="yn-statement" className="text-foreground font-medium">
          {m.statement}
        </Label>
        <Textarea
          id="yn-statement"
          value={getBody(block, lang).question ?? ''}
          onChange={(e) => {
            if (charsOk(e.target.value, QUIZ_LIMITS.QUESTION, getBody(block, lang).question)) {
              onChange(setQuestion(block, lang, e.target.value))
            }
          }}
          maxLength={QUIZ_LIMITS.QUESTION}
          placeholder={cm.statementPlaceholder}
          rows={3}
          className="resize-none rounded-xl"
        />
        <CharCount value={getBody(block, lang).question} max={QUIZ_LIMITS.QUESTION} />
      </section>

      <section className="space-y-2">
        <Label className="text-foreground font-medium">{m.correctAnswer}</Label>
        <div className="grid grid-cols-2 gap-2">
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              onClick={() => onChange({ ...block, correctAnswer: o.value })}
              aria-pressed={block.correctAnswer === o.value}
              className={cn(
                'h-11 rounded-xl border-2 text-sm font-semibold transition-all',
                // «Да» — зелёным, «Нет» — красным: цвет совпадает со смыслом ответа.
                block.correctAnswer === o.value
                  ? o.value
                    ? 'border-success bg-success/10 text-success'
                    : 'border-destructive bg-destructive/10 text-destructive'
                  : o.value
                    ? 'border-border bg-card text-muted-foreground hover:border-success/50'
                    : 'border-border bg-card text-muted-foreground hover:border-destructive/50',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </section>

      <QuizFeedbackFields block={block} onChange={onChange} />
    </div>
  )
}
